<script setup lang="ts">

import { Input, Question, DatePicker, RadioSet, Radio, ValidationError } from "./fields";
import {
    useAboutYouStore,
    MeasurementUnitType,
    SmokerStatus,
    MeasurementUnitLength,
    MeasurementUnitWeight,
} from "../stores/aboutYou";
import { tooltips } from "../tooltips/aboutYouTooltips";
import { useYourDetailsStore } from "../stores/yourDetails";
import { aboutYouValidator, smokerValidator } from "../utils/validators";
import Medical from "./medicalConditionForms/Medical.vue";
import Modal from "./Modal.vue";
import AlcoholCalculator from "./AlcoholCalculator.vue";
import {
    selectingHeightUnitWillClearTheOther,
    selectingWaistUnitWillClearTheOther,
    selectingWeightUnitWillClearTheOther,
} from "../composables/aboutYou";
import { MedicalCondition } from "../entities/conditions";
import { isDecimal, isNumber } from "@/utils";

const store = useAboutYouStore();
const yourDetailsStore = useYourDetailsStore();

function hideModal() {
    store.toggleAlcoholModal = false;
}

const updateConditions = (conditions: MedicalCondition[]) => {
    store.selectedMedicalConditions = [...conditions];
};

const aboutValidator = aboutYouValidator(store);
const smokeValidator = smokerValidator(store);
</script>

<template data-test="aboutYou">
    <div
        class="container disclaimer"
        data-test="disclaimer"
    >
        <div class="row">
            <div class="col-md-10">
                <p>
                    To find you the best annuity quotes we need your lifestyle information and medical history.
                </p><br>
                <p>It's important to answer this section accurately. Unlike insurance, you could receive a higher income if you have certain medical conditions and/or are overweight, or smoke. This is because providers consider your life expectancy.</p>
            </div>
        </div>
    </div>
    <h5 class="blue">
        Your lifestyle
    </h5>
    <Question
        name="smoker"
        type="group"
        label="Are you or have you ever been a regular daily smoker?"
        :tooltip="tooltips.smoker"
    >
        <RadioSet>
            <Radio
                v-model="store.smoker"
                title="Currently"
                radio-type="responsive"
                :value="SmokerStatus.Currently"
                :validation="smokeValidator.smoker"
            />
            <Radio
                v-model="store.smoker"
                title="Previously"
                radio-type="responsive"
                :value="SmokerStatus.Previously"
                :validation="smokeValidator.smoker"
            />
            <Radio
                v-model="store.smoker"
                title="Never"
                radio-type="responsive"
                :value="SmokerStatus.Never"
                :validation="smokeValidator.smoker"
            />
        </RadioSet>
    </Question>
    <Question
        v-if="store.smoker === SmokerStatus.Currently || store.smoker === SmokerStatus.Previously"
        type="group"
        name="smokingStart"
        label="Approximately when did you start smoking?"
    >
        <DatePicker
            v-model="store.smokingStart"
            :max-date="new Date()"
            :min-date="new Date(yourDetailsStore.dob)"
            :show-day-field="false"
            :validation="smokeValidator.smokingStart"
        />
    </Question>
    <Question
        v-if="store.smoker === SmokerStatus.Previously"
        type="group"
        name="smokingEnd"
        label="Approximately when did you stop smoking?"
    >
        <DatePicker
            v-model="store.smokingEnd"
            :max-date="new Date()"
            :min-date="new Date(store.smokingStart)"
            :show-day-field="false"
            :validation="smokeValidator.smokingEnd"
        />
    </Question>
    <div
        v-if="store.smoker === SmokerStatus.Currently || store.smoker === SmokerStatus.Previously"
        class="container disclaimer"
    >
        <div class="row">
            <div class="col-md-10">
                <p>Do/did you smoke:</p>
            </div>
        </div>
    </div>
    <template v-if="store.smoker === SmokerStatus.Currently || store.smoker === SmokerStatus.Previously">
        <Question
            type="group"
            name="cigarettes"
            label="Cigarettes"
        >
            <RadioSet>
                <Radio
                    v-model="store.cigarettes"
                    title="Yes"
                    :value="true"
                    :validation="smokeValidator.cigarettes"
                />
                <Radio
                    v-model="store.cigarettes"
                    title="No"
                    :value="false"
                    :validation="smokeValidator.cigarettes"
                />
            </RadioSet>
        </Question>
        <Question
            v-if="store.cigarettes"
            name="cigarettesPerDay"
            label="How many cigarettes do/did you smoke each day?"
            :tooltip="tooltips.cigarettesPerDay"
        >
            <Input
                v-model.number="store.cigarettesPerDay"
                placeholder="0"
                inputmode="numeric"
                type="number"
                :validation="smokeValidator.cigarettesPerDay"
            />
        </Question>
        <Question
            type="group"
            name="cigars"
            label="Cigars"
        >
            <RadioSet>
                <Radio
                    v-model="store.cigars"
                    title="Yes"
                    :value="true"
                    :validation="smokeValidator.cigars"
                />
                <Radio
                    v-model="store.cigars"
                    title="No"
                    :value="false"
                    :validation="smokeValidator.cigars"
                />
            </RadioSet>
        </Question>
        <Question
            v-if="store.cigars"
            name="cigarsPerDay"
            label="How many cigars do/did you smoke each day?"
            :tooltip="tooltips.cigarsPerDay"
        >
            <Input
                v-model.number="store.cigarsPerDay"
                placeholder="0"
                inputmode="numeric"
                type="number"
                :validation="smokeValidator.cigarsPerDay"
            />
        </Question>
        <Question
            type="group"
            name="rollingTobacco"
            label="Rolling tobacco"
        >
            <RadioSet>
                <Radio
                    v-model="store.rollingTobacco"
                    title="Yes"
                    :value="true"
                    :validation="smokeValidator.rollingTobacco"
                />
                <Radio
                    v-model="store.rollingTobacco"
                    title="No"
                    :value="false"
                    :validation="smokeValidator.rollingTobacco"
                />
            </RadioSet>
        </Question>
        <Question
            v-if="store.rollingTobacco"
            name="rollingTobaccoPerWeek"
            label="How many grams of tobacco do/did you smoke each week?"
            :tooltip="tooltips.rollingTobaccoPerWeek"
        >
            <Input
                v-model.number="store.rollingTobaccoPerWeek"
                placeholder="0"
                inputmode="numeric"
                type="number"
                :validation="smokeValidator.rollingTobaccoPerWeek"
            />
        </Question>
        <Question
            type="group"
            name="pipeTobacco"
            label="Pipe tobacco"
        >
            <RadioSet>
                <Radio
                    v-model="store.pipeTobacco"
                    title="Yes"
                    :value="true"
                    :validation="smokeValidator.pipeTobacco"
                />
                <Radio
                    v-model="store.pipeTobacco"
                    title="No"
                    :value="false"
                    :validation="smokeValidator.pipeTobacco"
                />
            </RadioSet>
        </Question>
        <Question
            v-if="store.pipeTobacco"
            name="pipeTobaccoPerWeek"
            label="How many grams of pipe tobacco do/did you smoke each week?"
            :tooltip="tooltips.pipeTobaccoPerWeek"
        >
            <Input
                v-model.number="store.pipeTobaccoPerWeek"
                placeholder="0"
                inputmode="numeric"
                type="number"
                :validation="smokeValidator.pipeTobaccoPerWeek"
            />
        </Question>
    </template>
    <ValidationError
        name="smoking error"
        :validation="smokeValidator.smokerType.type"
        :values="{smoker: store.smoker}"
    />
    <Question
        name="alcohol"
        label=""
        :tooltip="tooltips.alcohol"
    >
        <template #label>
            On average, how much alcohol do you drink in a week? Use our
            <a
                id="alcoholCalculator"
                href="#"
                @click.prevent="store.toggleAlcoholModal = true;"
            >alcohol unit calculator</a>
        </template>

        <Input
            id="alcohol"
            v-model.number="store.alcohol"
            placeholder="Units"
            class="col-md-12"
            input-class="input-width-auto"
            inputmode="numeric"
            type="number"
            :validation="aboutValidator.alcohol"
            @paste="isDecimal"
            @keypress="isDecimal"
        />
    </Question>
    <hr>
    <h5 class="blue">
        Your physique
    </h5>
    <Question
        type="group"
        name="yourHeight"
        label="How tall are you?"
        :tooltip="tooltips.height"
    >
        <Input
            v-if="store.heightUnit === MeasurementUnitType.Imperial"
            id="applicantHeightFeet"
            v-model.number="store.heightFeet"
            placeholder="Feet"
            hidden-label="Feet"
            inputmode="numeric"
            type="number"
            class="col-md-3"
            :append="MeasurementUnitLength.Feet"
            :filter="isNumber"
            :validation="aboutValidator.heightFeet"
        />
        <Input
            v-if="store.heightUnit === MeasurementUnitType.Imperial"
            id="applicantHeightInches"
            v-model.number="store.heightInches"
            placeholder="Inches"
            hidden-label="Inches"
            inputmode="numeric"
            type="number"
            class="col-md-3"
            :append="MeasurementUnitLength.Inches"
            :filter="isNumber"
            :validation="aboutValidator.heightInches"
        />

        <Input
            v-if="store.heightUnit === MeasurementUnitType.Metric"
            id="applicantHeightCentimetres"
            v-model.number="store.heightCentimetres"
            placeholder="Centimetres"
            hidden-label="Centimetres"
            inputmode="numeric"
            type="number"
            class="col-md-6"
            :append="MeasurementUnitLength.Centimetres"
            :filter="isNumber"
            :validation="aboutValidator.heightCentimetres"
        />
        <RadioSet col="col-md-4">
            <Radio
                v-model="store.heightUnit"
                child-class="pill-start"
                radio-type="pill"
                title="Imperial"
                :value="MeasurementUnitType.Imperial"
                :validation="aboutValidator.heightUnit"
                @click="selectingHeightUnitWillClearTheOther(store)"
            />
            <Radio
                v-model="store.heightUnit"
                child-class="pill-end"
                radio-type="pill"
                title="Metric"
                :value="MeasurementUnitType.Metric"
                :validation="aboutValidator.heightUnit"
                @click="selectingHeightUnitWillClearTheOther(store)"
            />
        </RadioSet>
    </Question>
    <Question
        type="group"
        name="yourWeight"
        label="What do you weigh?"
        :tooltip="tooltips.weight"
    >
        <Input
            v-if="store.weightUnit === MeasurementUnitType.Imperial"
            id="applicantWeightStone"
            v-model.number="store.weightStone"
            placeholder="Stone"
            hidden-label="Stone"
            inputmode="numeric"
            type="number"
            class="col-md-3"
            :append="MeasurementUnitWeight.Stone"
            :filter="isNumber"
            :validation="aboutValidator.weightStone"
        />
        <Input
            v-if="store.weightUnit === MeasurementUnitType.Imperial"
            id="applicantWeightPounds"
            v-model.number="store.weightPounds"
            placeholder="Pounds"
            hidden-label="Pounds"
            inputmode="numeric"
            type="number"
            class="col-md-3"
            :append="MeasurementUnitWeight.Pounds"
            :filter="isNumber"
            :validation="aboutValidator.weightPounds"
        />
        <Input
            v-if="store.weightUnit === MeasurementUnitType.Metric"
            id="applicantWeightKg"
            v-model.number="store.weightKg"
            placeholder="Kilogram"
            hidden-label="Kilogram"
            inputmode="numeric"
            type="number"
            class="col-md-6"
            :append="MeasurementUnitWeight.Kilogram"
            :filter="isNumber"
            :validation="aboutValidator.weightKg"
        />
        <RadioSet
            col="col-md-4"
        >
            <Radio
                v-model="store.weightUnit"
                child-class="pill-start"
                radio-type="pill"
                title="Imperial"
                :value="MeasurementUnitType.Imperial"
                :validation="aboutValidator.weightUnit"
                @click="selectingWeightUnitWillClearTheOther(store)"
            />
            <Radio
                v-model="store.weightUnit"
                child-class="pill-end"
                radio-type="pill"
                title="Metric"
                :value="MeasurementUnitType.Metric"
                :validation="aboutValidator.weightUnit"
                @click="selectingWeightUnitWillClearTheOther(store)"
            />
        </RadioSet>
    </Question>
    <Question
        type="group"
        name="yourWaist"
        label="What is your waist measurement? (optional)"
        :tooltip="tooltips.waist"
    >
        <Input
            v-if="store.waistUnit === MeasurementUnitType.Imperial"
            id="applicantWaistInches"
            v-model.number="store.waistInches"
            placeholder="Inches"
            hidden-label="Inches"
            inputmode="numeric"
            type="number"
            class="col-md-6"
            :append="MeasurementUnitLength.Inches"
            :filter="isNumber"
            :validation="aboutValidator.waistInches"
        />
        <Input
            v-if="store.waistUnit === MeasurementUnitType.Metric"
            id="applicantWaistCentimetres"
            v-model.number="store.waistCentimetres"
            placeholder="Centimetres"
            hidden-label="Centimetres"
            inputmode="numeric"
            type="number"
            class="col-md-6"
            :append="MeasurementUnitLength.Centimetres"
            :filter="isNumber"
            :validation="aboutValidator.waistCentimetres"
        />
        <RadioSet
            col="col-md-4"
        >
            <Radio
                v-model="store.waistUnit"
                child-class="pill-start"
                radio-type="pill"
                title="Imperial"
                :value="MeasurementUnitType.Imperial"
                @click="selectingWaistUnitWillClearTheOther(store)"
            />
            <Radio
                v-model="store.waistUnit"
                child-class="pill-end"
                radio-type="pill"
                title="Metric"
                :value="MeasurementUnitType.Metric"
                @click="selectingWaistUnitWillClearTheOther(store)"
            />
        </RadioSet>
    </Question>
    <hr>
    <h5 class="blue">
        Your health
    </h5>
    <div class="container disclaimer">
        <div class="row">
            <div class="col-md-10">
                <p> Please be completely honest about any conditions you have or have had in the past. If you are unsure, please declare the condition. The provider will assess your declared medical condition(s) later to decide if the income quoted is correct.</p><br>
                <p>There is no limit to how far back you should go in your history. For example, if you had cancer as a child, you should declare this even if you have had no recurrence since then. </p><br>
                <p>Remember, you could receive a higher income if you have certain medical conditions. This is because the providers consider your life expectancy.</p><br>
            </div>
        </div>
    </div>
    <Question
        type="group"
        name="medical"
        label="Do you have, or have you ever had any medical conditions?"
        :tooltip="tooltips.medical"
    >
        <RadioSet>
            <Radio
                id="applicant_medicalconditions_ind_0"
                v-model="store.medical"
                title="Yes"
                :value="true"
                :validation="aboutValidator.medical"
            />
            <Radio
                id="applicant_medicalconditions_ind_1"
                v-model="store.medical"
                title="No"
                :value="false"
                :validation="aboutValidator.medical"
            />
        </RadioSet>
    </Question>
    <Modal
        id="applicant-alcohol-calculator"
        open-focus="close-applicant-alcohol-calculator"
        close-focus="alcoholCalculator"
        header="Alcohol calculator"
        :show="store.toggleAlcoholModal"
        @close="hideModal"
    >
        <template #body>
            <AlcoholCalculator
                v-model="store.alcohol"
                @close="hideModal"
            />
        </template>
    </Modal>
    <Medical
        v-if="store.medical"
        :person-details="yourDetailsStore.getPersonDataForMedical"
        :medical-conditions="store.selectedMedicalConditions"
        @update:conditions="updateConditions"
    />
    <ValidationError
        v-if="store.medical && store.selectedMedicalConditions.length === 0"
        :validation="aboutValidator.medicalQuantity"
        class="alert alert-danger text-center"
        :values="{}"
        name="no declared conditions"
    />
</template>

<style lang="scss">
.disclaimer{
    background-color: #ffffff;
    padding: 20px !important;
    margin-bottom: 20px;
}
</style>
